import type { DashboardLimits } from "@estelle/domain";

/**
 * What each dashboard metric counts, how, and whether a value is healthy
 * (#638), behind the info button beside it.
 *
 * "Healthy" is read from the limits in Settings › Dashboard, the same ones the
 * colours come from, so the note and the colour cannot disagree. A metric with
 * no limit says so rather than inventing a target.
 */
export type MetricKey =
  | "orders"
  | "revenue"
  | "average_order_value"
  | "rto_rate"
  | "delivery_success"
  | "first_attempt"
  | "cod_share"
  | "cancellation_rate"
  | "order_to_ship"
  | "ship_to_deliver";

export interface MetricNote {
  counts: string;
  how: string;
  healthy: string;
}

const NO_LIMIT =
  "No limit is set for this one. Compare it with the period before, shown under the figure.";

export function metricNote(
  key: MetricKey,
  limits: DashboardLimits,
): MetricNote {
  switch (key) {
    case "orders":
      return {
        counts: "Every order placed in the period, cancelled ones included.",
        how: "Counted by the day it was placed, midnight to midnight, India time.",
        healthy: NO_LIMIT,
      };
    case "revenue":
      return {
        counts: "The total of those orders, as they were placed.",
        how: "Refunds and cancellations are not taken off; Reports › Sales can leave cancelled orders out.",
        healthy: NO_LIMIT,
      };
    case "average_order_value":
      return {
        counts: "What an order was worth on average.",
        how: "Revenue divided by the number of orders.",
        healthy: NO_LIMIT,
      };
    case "rto_rate": {
      const { amber, red } = limits.rto_rate;
      return {
        counts:
          "Of the shipments that finished, the share that came back to the warehouse (RTO).",
        how: "Shipments that ended as RTO ÷ shipments delivered or RTO, by the day they finished.",
        healthy: `Healthy at ${String(amber)}% or less. Amber over ${String(amber)}%, red over ${String(red)}% (Settings › Dashboard).`,
      };
    }
    case "delivery_success": {
      const { amber, red } = limits.rto_rate;
      return {
        counts: "Of the shipments that finished, the share delivered.",
        how: "Delivered ÷ delivered or RTO. It is 100% minus the RTO rate.",
        healthy: `Healthy at ${String(100 - amber)}% or more, since RTO turns amber over ${String(amber)}% and red over ${String(red)}%.`,
      };
    }
    case "first_attempt":
      return {
        counts: "Delivered on the courier's first try, out of all delivered.",
        how: "Delivered on the first attempt ÷ delivered.",
        healthy: NO_LIMIT,
      };
    case "cod_share":
      return {
        counts: "The share of orders paid on delivery, and paid up front.",
        how: "Cash-on-delivery orders ÷ orders with a known payment method.",
        healthy:
          "No right answer. A higher COD share means more confirmation calls and more RTO risk.",
      };
    case "cancellation_rate": {
      const { amber, red } = limits.cancellation_rate;
      return {
        counts: "The share of orders placed in the period that were cancelled.",
        how: "Cancelled ÷ placed.",
        healthy: `Healthy at ${String(amber)}% or less. Amber over ${String(amber)}%, red over ${String(red)}% (Settings › Dashboard).`,
      };
    }
    case "order_to_ship":
      return {
        counts: "How long an order waited before its shipment left.",
        how: "The median, for shipments that left in the period: half were faster, half slower.",
        healthy: NO_LIMIT,
      };
    case "ship_to_deliver":
      return {
        counts: "How long a shipment took to reach the customer.",
        how: "The median, for shipments delivered in the period: half were faster, half slower.",
        healthy: NO_LIMIT,
      };
  }
}
